import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Heading,
  Text,
  Flex,
  Grid,
  GridItem,
  Stack,
  Button,
  useColorModeValue,
  Card,
  CardHeader,
  CardBody,
  Select,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  HStack,
  Badge,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Tooltip,
  Spinner,
  useToast,
  InputGroup,
  InputLeftElement,
  Divider,
  Tag,
  TagLabel,
  Alert,
  AlertIcon,
  Progress
} from '@chakra-ui/react';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiDownload,
  FiUpload,
  FiFilter,
  FiCalendar,
  FiSearch,
  FiArrowUp,
  FiArrowDown,
  FiDollarSign,
  FiMoreVertical,
  FiFileText,
  FiPieChart,
  FiBarChart2
} from 'react-icons/fi';
import {
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend,
} from 'recharts';
import { useUser } from '../Context/UserContext';

const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_VERCEL_API_URL
    : process.env.REACT_APP_LOCAL_API_URL;

const PIE_COLORS = ['#38A169', '#3182CE', '#D69E2E', '#E53E3E', '#9F7AEA', '#ED8936', '#DD6B20', '#718096'];

// Helper function to format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

// Helper function to get current date in YYYY-MM-DD format
const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Define transaction categories
const categorias = {
  receitas: [
    'Pagamentos de Serviços',
    'Comissões',
    'Taxas de Plataforma',
    'Assinaturas Premium',
    'Outros Recebimentos'
  ],
  despesas: [
    'Pagamentos a Parceiros',
    'Marketing e Publicidade',
    'Salários e Equipe',
    'Infraestrutura de TI',
    'Despesas Administrativas',
    'Taxas Bancárias',
    'Impostos',
    'Outras Despesas'
  ]
};

// Map payment API response to transaction format
const mapPaymentToTransaction = (payment) => {
  const isRefund = payment.status === 'refunded' || payment.status === 'chargedback';
  return {
    id: payment.id,
    data: payment.createdAt?.split('T')[0] || '',
    descricao: payment.description || 'Pagamento',
    categoria: isRefund ? 'Estornos' : 'Assinaturas Premium',
    tipo: isRefund ? 'despesa' : 'receita',
    valor: payment.amount || 0,
    status: payment.status,
    isFromApi: true,
  };
};

// Main FluxoCaixa Component
const FluxoCaixa = () => {
  // Theme colors and styles
  const cardBg = useColorModeValue('white', 'gray.700');
  const headerBg = useColorModeValue('yellow.50', 'gray.700');
  const receitaColor = useColorModeValue('green.500', 'green.300');
  const despesaColor = useColorModeValue('red.500', 'red.300');
  const hoverBg = useColorModeValue('gray.50', 'gray.600');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.700');
  
  const { user } = useUser();
  const toast = useToast();
  const modal = useDisclosure();
  const deleteConfirmModal = useDisclosure();
  const filterModal = useDisclosure();
  const [transacoes, setTransacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTransacao, setEditingTransacao] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [filtros, setFiltros] = useState({
    dataInicio: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0], // First day of current month
    dataFim: new Date().toISOString().split('T')[0], // Today
    tipo: '',
    categoria: '',
    busca: ''
  });
  const [estatisticas, setEstatisticas] = useState({
    totalReceitas: 0,
    totalDespesas: 0,
    saldoMensal: 0
  });
  const [novaTransacao, setNovaTransacao] = useState({
    data: getTodayDate(),
    descricao: '',
    categoria: '',
    tipo: 'receita',
    valor: '',
  });
  
  // Fetch transactions from payment API
  useEffect(() => {
    const fetchTransacoes = async () => {
      try {
        const token = localStorage.getItem('userToken_TOVIAPPS');
        const res = await fetch(`${API_BASE_URL}api/payment/admin/history?limit=50`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Falha ao buscar dados');
        const data = await res.json();
        const mapped = (data.payments || []).map(mapPaymentToTransaction);
        setTransacoes(mapped);
        calcularEstatisticas(mapped);
      } catch (error) {
        console.error('Erro ao buscar transações:', error);
        toast({
          title: 'Erro ao carregar dados',
          description: 'Não foi possível carregar as transações financeiras.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTransacoes();
  }, [toast]);

  // Calculate statistics from transactions
  const calcularEstatisticas = (listaTransacoes) => {
    const totalReceitas = listaTransacoes
      .filter(t => t.tipo === 'receita')
      .reduce((sum, t) => sum + t.valor, 0);
    
    const totalDespesas = listaTransacoes
      .filter(t => t.tipo === 'despesa')
      .reduce((sum, t) => sum + t.valor, 0);
      
    setEstatisticas({
      totalReceitas,
      totalDespesas,
      saldoMensal: totalReceitas - totalDespesas
    });
  };

  // Handle form change for new/edited transaction
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setNovaTransacao(prev => ({ ...prev, [name]: value }));
  };

  // Handle number input change for amount
  const handleValorChange = (value) => {
    setNovaTransacao(prev => ({ ...prev, valor: value }));
  };

  // Handle filter changes
  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros(prev => ({ ...prev, [name]: value }));
  };

  // Apply filters to transactions
  const aplicarFiltros = () => {
    filterModal.onClose();
    const filteredTransactions = transacoes.filter(t => {
      const matchesDate = new Date(t.data) >= new Date(filtros.dataInicio) && 
                          new Date(t.data) <= new Date(filtros.dataFim);
      const matchesTipo = filtros.tipo ? t.tipo === filtros.tipo : true;
      const matchesCategoria = filtros.categoria ? t.categoria === filtros.categoria : true;
      const matchesBusca = filtros.busca ? 
        t.descricao.toLowerCase().includes(filtros.busca.toLowerCase()) : true;
      
      return matchesDate && matchesTipo && matchesCategoria && matchesBusca;
    });
    
    setTransacoes(filteredTransactions);
    calcularEstatisticas(filteredTransactions);
  };

  // Reset filters
  const limparFiltros = () => {
    setFiltros({
      dataInicio: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
      dataFim: new Date().toISOString().split('T')[0],
      tipo: '',
      categoria: '',
      busca: ''
    });
    
    filterModal.onClose();
  };

  // Handle save transaction (new or edit)
  const salvarTransacao = () => {
    // Validation
    if (!novaTransacao.descricao || !novaTransacao.categoria || !novaTransacao.valor) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Preencha todos os campos obrigatórios.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      if (editingTransacao) {
        // Update existing transaction
        const updatedTransacoes = transacoes.map(t => 
          t.id === editingTransacao.id ? { ...novaTransacao, id: t.id } : t
        );
        setTransacoes(updatedTransacoes);
        calcularEstatisticas(updatedTransacoes);
        
        toast({
          title: 'Transação atualizada',
          description: 'A transação foi atualizada com sucesso.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Create new transaction
        const newId = Math.max(...transacoes.map(t => t.id), 0) + 1;
        const newTransaction = { ...novaTransacao, id: newId };
        const updatedTransacoes = [...transacoes, newTransaction];
        setTransacoes(updatedTransacoes);
        calcularEstatisticas(updatedTransacoes);
        
        toast({
          title: 'Transação adicionada',
          description: 'A nova transação foi adicionada com sucesso.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      
      // Reset form and close modal
      setNovaTransacao({
        data: getTodayDate(),
        descricao: '',
        categoria: '',
        tipo: 'receita',
        valor: '',
      });
      setEditingTransacao(null);
      modal.onClose();
      
    } catch (error) {
      console.error('Erro ao salvar transação:', error);
      toast({
        title: 'Erro ao salvar',
        description: 'Ocorreu um erro ao tentar salvar a transação.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle edit transaction
  const editarTransacao = (transacao) => {
    setEditingTransacao(transacao);
    setNovaTransacao({ ...transacao });
    modal.onOpen();
  };

  // Confirm delete transaction
  const confirmarExclusao = (id) => {
    setDeletingId(id);
    deleteConfirmModal.onOpen();
  };

  // Delete transaction
  const excluirTransacao = () => {
    try {
      const updatedTransacoes = transacoes.filter(t => t.id !== deletingId);
      setTransacoes(updatedTransacoes);
      calcularEstatisticas(updatedTransacoes);
      
      toast({
        title: 'Transação excluída',
        description: 'A transação foi excluída com sucesso.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      deleteConfirmModal.onClose();
      setDeletingId(null);
      
    } catch (error) {
      console.error('Erro ao excluir transação:', error);
      toast({
        title: 'Erro ao excluir',
        description: 'Ocorreu um erro ao tentar excluir a transação.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Create new transaction
  const novaTransacaoClick = () => {
    setEditingTransacao(null);
    setNovaTransacao({
      data: getTodayDate(),
      descricao: '',
      categoria: '',
      tipo: 'receita',
      valor: '',
    });
    modal.onOpen();
  };

  // Export data to CSV
  const exportarCSV = () => {
    try {
      const headers = ['ID', 'Data', 'Descrição', 'Categoria', 'Tipo', 'Valor'];
      const csvContent = [
        headers.join(','),
        ...transacoes.map(t => [
          t.id,
          t.data,
          `"${t.descricao}"`, // Wrap description in quotes to handle commas
          `"${t.categoria}"`,
          t.tipo === 'receita' ? 'Receita' : 'Despesa',
          t.valor.toString().replace('.', ',') // Use comma as decimal separator for CSV
        ].join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `fluxo-caixa-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: 'Exportação concluída',
        description: 'Os dados foram exportados em formato CSV.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
    } catch (error) {
      console.error('Erro ao exportar CSV:', error);
      toast({
        title: 'Erro na exportação',
        description: 'Não foi possível exportar os dados.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box
        width="100%"
        height="70vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
      >
        <Spinner size="xl" color="yellow.500" thickness="4px" mb={4} />
        <Text>Carregando dados financeiros...</Text>
      </Box>
    );
  }

  // Main render
  return (
    <>
      <Box width="100%" px={{ base: 2, md: 4 }}>
        {/* Header Section */}
        <Flex 
          direction={{ base: "column", md: "row" }} 
          justify="space-between"
          align={{ base: "flex-start", md: "center" }}
          mb={6}
        >
          <Box mb={{ base: 4, md: 0 }}>
            <Heading as="h1" size="xl" mb={2} color="gray.700">
              Fluxo de Caixa
            </Heading>
            <Text color="gray.500" fontSize="md">
              Controle de receitas e despesas
            </Text>
          </Box>
          
          <HStack spacing={3}>
            <Button
              colorScheme="yellow"
              leftIcon={<FiFilter />}
              onClick={filterModal.onOpen}
              size="md"
            >
              Filtrar
            </Button>
            
            <Button
              colorScheme="green"
              leftIcon={<FiPlus />}
              onClick={novaTransacaoClick}
              size="md"
            >
              Nova Transação
            </Button>
            
            <Tooltip label="Exportar para CSV">
              <IconButton
                aria-label="Exportar para CSV"
                icon={<FiDownload />}
                onClick={exportarCSV}
                colorScheme="gray"
                variant="outline"
              />
            </Tooltip>
          </HStack>
        </Flex>

        {/* Statistics Cards */}
        <Grid
          templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
          gap={5}
          mb={8}
        >
          <Card bg={cardBg} boxShadow="md" borderRadius="lg" overflow="hidden">
            <CardBody p={5}>
              <Flex direction="column">
                <Text color="gray.500" fontSize="sm" mb={1}>Receitas Totais</Text>
                <Text color={receitaColor} fontSize="2xl" fontWeight="bold">
                  {formatCurrency(estatisticas.totalReceitas)}
                </Text>
                <HStack mt={2}>
                  <FiArrowUp color="green" />
                  <Text fontSize="sm" color="gray.500">Entradas no período</Text>
                </HStack>
              </Flex>
            </CardBody>
          </Card>
          
          <Card bg={cardBg} boxShadow="md" borderRadius="lg" overflow="hidden">
            <CardBody p={5}>
              <Flex direction="column">
                <Text color="gray.500" fontSize="sm" mb={1}>Despesas Totais</Text>
                <Text color={despesaColor} fontSize="2xl" fontWeight="bold">
                  {formatCurrency(estatisticas.totalDespesas)}
                </Text>
                <HStack mt={2}>
                  <FiArrowDown color="red" />
                  <Text fontSize="sm" color="gray.500">Saídas no período</Text>
                </HStack>
              </Flex>
            </CardBody>
          </Card>
          
          <Card bg={cardBg} boxShadow="md" borderRadius="lg" overflow="hidden">
            <CardBody p={5}>
              <Flex direction="column">
                <Text color="gray.500" fontSize="sm" mb={1}>Saldo do Período</Text>
                <Text 
                  color={estatisticas.saldoMensal >= 0 ? receitaColor : despesaColor} 
                  fontSize="2xl" 
                  fontWeight="bold"
                >
                  {formatCurrency(estatisticas.saldoMensal)}
                </Text>
                <HStack mt={2}>
                  {estatisticas.saldoMensal >= 0 ? (
                    <FiArrowUp color="green" />
                  ) : (
                    <FiArrowDown color="red" />
                  )}
                  <Text fontSize="sm" color="gray.500">
                    Saldo {estatisticas.saldoMensal >= 0 ? 'positivo' : 'negativo'}
                  </Text>
                </HStack>
              </Flex>
            </CardBody>
          </Card>
        </Grid>
        
        {/* Transactions Table */}
        <Card bg={cardBg} boxShadow="md" borderRadius="lg" overflow="hidden" mb={6}>
          <CardHeader bg={headerBg} py={4} px={6}>
            <Flex justifyContent="space-between" alignItems="center">
              <Heading size="md">Transações</Heading>
              <Text fontSize="sm" color="gray.500">
                {transacoes.length} registro{transacoes.length !== 1 ? 's' : ''}
              </Text>
            </Flex>
          </CardHeader>
          
          <CardBody px={0} py={0}>
            <Box overflowX="auto">
              <Table variant="simple">
                <Thead bg={tableHeaderBg}>
                  <Tr>
                    <Th>Data</Th>
                    <Th>Descrição</Th>
                    <Th>Categoria</Th>
                    <Th isNumeric>Valor</Th>
                    <Th width="120px">Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {transacoes.length === 0 ? (
                    <Tr>
                      <Td colSpan={5} textAlign="center" py={6}>
                        <Text color="gray.500">Nenhuma transação encontrada.</Text>
                      </Td>
                    </Tr>
                  ) : (
                    transacoes.map((transacao) => (
                      <Tr 
                        key={transacao.id}
                        _hover={{ bg: hoverBg }}
                        transition="background-color 0.2s"
                      >
                        <Td>{new Date(transacao.data).toLocaleDateString('pt-BR')}</Td>
                        <Td maxW="300px" isTruncated>{transacao.descricao}</Td>
                        <Td>
                          <Badge 
                            colorScheme={transacao.tipo === 'receita' ? 'green' : 'red'} 
                            variant="subtle"
                            px={2}
                            py={1}
                            borderRadius="full"
                          >
                            {transacao.categoria}
                          </Badge>
                        </Td>
                        <Td isNumeric>
                          <Text
                            color={transacao.tipo === 'receita' ? receitaColor : despesaColor}
                            fontWeight="medium"
                          >
                            {transacao.tipo === 'receita' ? '+ ' : '- '}
                            {formatCurrency(transacao.valor)}
                          </Text>
                        </Td>
                        <Td>
                          <HStack spacing={2}>
                            <IconButton
                              aria-label="Editar"
                              icon={<FiEdit2 />}
                              size="sm"
                              colorScheme="blue"
                              variant="ghost"
                              onClick={() => editarTransacao(transacao)}
                            />
                            <IconButton
                              aria-label="Excluir"
                              icon={<FiTrash2 />}
                              size="sm"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => confirmarExclusao(transacao.id)}
                            />
                          </HStack>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>
          </CardBody>
        </Card>
        
        {/* Charts Section */}
        <Card bg={cardBg} boxShadow="md" borderRadius="lg" overflow="hidden" mb={6}>
          <CardHeader bg={headerBg} py={4} px={6}>
            <Flex justifyContent="space-between" alignItems="center">
              <Heading size="md">Análise Gráfica</Heading>
              <HStack>
                <FiPieChart />
                <FiBarChart2 style={{ marginLeft: '8px' }} />
              </HStack>
            </Flex>
          </CardHeader>
          
          <CardBody>
            <Tabs isFitted colorScheme="yellow">
              <TabList mb={4}>
                <Tab>Balanço Geral</Tab>
                <Tab>Por Categoria</Tab>
                <Tab>Histórico Mensal</Tab>
              </TabList>
              
              <TabPanels>
                <TabPanel>
                  <Box height="300px">
                    {transacoes.length === 0 ? (
                      <Flex height="100%" alignItems="center" justifyContent="center">
                        <Text color="gray.500">Sem dados para exibir</Text>
                      </Flex>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={[
                          { name: 'Receitas', valor: estatisticas.totalReceitas },
                          { name: 'Despesas', valor: estatisticas.totalDespesas },
                          { name: 'Saldo', valor: estatisticas.saldoMensal },
                        ]}>
                          <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                          <XAxis dataKey="name" />
                          <YAxis tickFormatter={(v) => formatCurrency(v)} />
                          <RechartsTooltip formatter={(v) => formatCurrency(v)} />
                          <Bar dataKey="valor" radius={[6, 6, 0, 0]}>
                            <Cell fill="#38A169" />
                            <Cell fill="#E53E3E" />
                            <Cell fill={estatisticas.saldoMensal >= 0 ? '#3182CE' : '#E53E3E'} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </Box>
                </TabPanel>

                <TabPanel>
                  <Box height="300px">
                    {(() => {
                      const catMap = {};
                      transacoes.forEach(t => {
                        if (!catMap[t.categoria]) catMap[t.categoria] = { name: t.categoria, value: 0 };
                        catMap[t.categoria].value += t.valor;
                      });
                      const catData = Object.values(catMap).sort((a, b) => b.value - a.value);
                      if (catData.length === 0) return <Flex height="100%" alignItems="center" justifyContent="center"><Text color="gray.500">Sem dados para exibir</Text></Flex>;
                      return (
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={catData} cx="50%" cy="50%" outerRadius={100} dataKey="value"
                              label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`} labelLine={false}>
                              {catData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                            </Pie>
                            <RechartsTooltip formatter={(v) => formatCurrency(v)} />
                          </PieChart>
                        </ResponsiveContainer>
                      );
                    })()}
                  </Box>
                </TabPanel>

                <TabPanel>
                  <Box height="300px">
                    {(() => {
                      const monthMap = {};
                      transacoes.forEach(t => {
                        const d = new Date(t.data);
                        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                        if (!monthMap[key]) monthMap[key] = { month: key, receitas: 0, despesas: 0 };
                        if (t.tipo === 'receita') monthMap[key].receitas += t.valor;
                        else monthMap[key].despesas += t.valor;
                      });
                      const monthData = Object.values(monthMap).sort((a, b) => a.month.localeCompare(b.month));
                      if (monthData.length === 0) return <Flex height="100%" alignItems="center" justifyContent="center"><Text color="gray.500">Sem dados para exibir</Text></Flex>;
                      return (
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={monthData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                            <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `R$${v}`} />
                            <RechartsTooltip formatter={(v) => formatCurrency(v)} />
                            <Legend />
                            <Line type="monotone" dataKey="receitas" name="Receitas" stroke="#38A169" strokeWidth={2} dot={{ r: 4 }} />
                            <Line type="monotone" dataKey="despesas" name="Despesas" stroke="#E53E3E" strokeWidth={2} dot={{ r: 4 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      );
                    })()}
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </CardBody>
        </Card>
      </Box>

      {/* Transaction Modal */}
      <Modal isOpen={modal.isOpen} onClose={modal.onClose} size="md">
        <ModalOverlay backdropFilter="blur(2px)" />
        <ModalContent>
          <ModalHeader>
            {editingTransacao ? 'Editar Transação' : 'Nova Transação'}
          </ModalHeader>
          <ModalCloseButton />
          
          <ModalBody>
            <Stack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Tipo</FormLabel>
                <Select
                  name="tipo"
                  value={novaTransacao.tipo}
                  onChange={handleFormChange}
                >
                  <option value="receita">Receita</option>
                  <option value="despesa">Despesa</option>
                </Select>
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>Data</FormLabel>
                <Input
                  type="date"
                  name="data"
                  value={novaTransacao.data}
                  onChange={handleFormChange}
                />
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>Descrição</FormLabel>
                <Input
                  name="descricao"
                  value={novaTransacao.descricao}
                  onChange={handleFormChange}
                  placeholder="Descreva a transação"
                />
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>Categoria</FormLabel>
                <Select
                  name="categoria"
                  value={novaTransacao.categoria}
                  onChange={handleFormChange}
                  placeholder="Selecione uma categoria"
                >
                  {novaTransacao.tipo === 'receita' ? (
                    categorias.receitas.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))
                  ) : (
                    categorias.despesas.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))
                  )}
                </Select>
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>Valor (R$)</FormLabel>
                <NumberInput
                  min={0}
                  precision={2}
                  step={0.01}
                  value={novaTransacao.valor}
                  onChange={handleValorChange}
                >
                  <InputGroup>
                    <InputLeftElement
                      pointerEvents="none"
                      children={<FiDollarSign color="gray.300" />}
                    />
                    <NumberInputField
                      pl="8"
                      placeholder="0,00"
                    />
                  </InputGroup>
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </Stack>
          </ModalBody>
          
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={modal.onClose}>
              Cancelar
            </Button>
            <Button colorScheme="yellow" onClick={salvarTransacao}>
              {editingTransacao ? 'Atualizar' : 'Adicionar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      
      {/* Filter Modal */}
      <Modal isOpen={filterModal.isOpen} onClose={filterModal.onClose} size="md">
        <ModalOverlay backdropFilter="blur(2px)" />
        <ModalContent>
          <ModalHeader>Filtrar Transações</ModalHeader>
          <ModalCloseButton />
          
          <ModalBody>
            <Stack spacing={4}>
              <Grid templateColumns="1fr 1fr" gap={4}>
                <FormControl>
                  <FormLabel>Data Inicial</FormLabel>
                  <Input
                    type="date"
                    name="dataInicio"
                    value={filtros.dataInicio}
                    onChange={handleFiltroChange}
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel>Data Final</FormLabel>
                  <Input
                    type="date"
                    name="dataFim"
                    value={filtros.dataFim}
                    onChange={handleFiltroChange}
                  />
                </FormControl>
              </Grid>
              
              <FormControl>
                <FormLabel>Tipo</FormLabel>
                <Select
                  name="tipo"
                  value={filtros.tipo}
                  onChange={handleFiltroChange}
                  placeholder="Todos os tipos"
                >
                  <option value="receita">Receitas</option>
                  <option value="despesa">Despesas</option>
                </Select>
              </FormControl>
              
              <FormControl>
                <FormLabel>Categoria</FormLabel>
                <Select
                  name="categoria"
                  value={filtros.categoria}
                  onChange={handleFiltroChange}
                  placeholder="Todas as categorias"
                >
                  <optgroup label="Receitas">
                    {categorias.receitas.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Despesas">
                    {categorias.despesas.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </optgroup>
                </Select>
              </FormControl>
              
              <FormControl>
                <FormLabel>Busca por descrição</FormLabel>
                <InputGroup>
                  <InputLeftElement
                    pointerEvents="none"
                    children={<FiSearch color="gray.300" />}
                  />
                  <Input
                    name="busca"
                    value={filtros.busca}
                    onChange={handleFiltroChange}
                    placeholder="Buscar na descrição..."
                  />
                </InputGroup>
              </FormControl>
            </Stack>
          </ModalBody>
          
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={limparFiltros}>
              Limpar Filtros
            </Button>
            <Button colorScheme="yellow" onClick={aplicarFiltros}>
              Aplicar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      
      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteConfirmModal.isOpen} onClose={deleteConfirmModal.onClose} isCentered size="sm">
        <ModalOverlay backdropFilter="blur(2px)" />
        <ModalContent>
          <ModalHeader>Confirmar Exclusão</ModalHeader>
          <ModalCloseButton />
          
          <ModalBody>
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              Esta ação não poderá ser desfeita. Deseja prosseguir?
            </Alert>
          </ModalBody>
          
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={deleteConfirmModal.onClose}>
              Cancelar
            </Button>
            <Button colorScheme="red" onClick={excluirTransacao}>
              Excluir
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default FluxoCaixa;